import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Select, Typography } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import { useTranslation } from 'react-i18next';

import type { ValidationMessage } from '@/api/workflows';

const { Text } = Typography;

/**
 * Every rule a field or cell can raise, in the order an originator meets them. Mirrors the BE
 * allow-list (`ValidationMessage.AllowedKeys`); the label is the generic message it replaces.
 */
export const VALIDATION_MESSAGE_KEYS = [
  'required',
  'regex.format',
  'length.min',
  'length.exact',
  'length.max',
  'number.min',
  'number.max',
  'displayMask',
  'sin.length',
  'sin.firstDigit',
  'sin.checksum',
  'email.format',
  'phone.length',
  'postal.format',
  'postal.invalidLetter',
  'date.invalid',
  'date.noFuture',
  'date.minAge',
  'date.maxAge',
  'date.noPast',
  'date.mustFollow',
  'uniqueness',
  'sum',
  'sum.amount',
] as const;

/** Rows the editor holds; a row without English text is dropped on save ("no override"). */
export function cleanValidationMessages(
  rows: ValidationMessage[] | undefined,
): ValidationMessage[] {
  const seen = new Set<string>();
  return (rows ?? []).flatMap((r) => {
    const en = r?.en?.trim();
    if (!r?.key || !en || seen.has(r.key)) return [];
    seen.add(r.key);
    return [{ key: r.key, en, fr: r.fr?.trim() || null }];
  });
}

/**
 * "Error messages" — the field's (or column's) own wording for any rule it can break, in both
 * languages: BMO words most errors per field ("Enter amount to continue", "Please enter a valid
 * fund number."). A rule without a row keeps the generic message. `name` is the Form.List path:
 * `'validationMessages'` on a field, `[colIndex, 'validationMessages']` inside the column list.
 */
export function ValidationMessagesEditor({ name }: { name: NamePath }) {
  const { t } = useTranslation();
  const options = VALIDATION_MESSAGE_KEYS.map((k) => ({
    value: k,
    label: `${k} — ${k === 'sum' ? t('workflow.builder.properties.validationMessages.sumKey') : k === 'sum.amount' ? t('workflow.builder.properties.validationMessages.sumAmountKey') : t(`formRenderer.validation.${k}`, { count: 5, years: 18, field: '…', peer: '…', step: '…', letters: 'D', min: '…', max: '…' })}`,
  }));
  return (
    <Form.List name={name as never}>
      {(rows, { add, remove }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          <Text strong style={{ fontSize: 12 }}>
            {t('workflow.builder.properties.validationMessages.title')}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('workflow.builder.properties.validationMessages.hint')}
          </Text>
          {rows.map((row) => (
            <div
              key={row.key}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                padding: 8,
                display: 'grid',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', gap: 6 }}>
                <Form.Item name={[row.name, 'key']} style={{ flex: 1, marginBottom: 0 }}>
                  <Select
                    options={options}
                    aria-label={t('workflow.builder.properties.validationMessages.rule')}
                    popupMatchSelectWidth={false}
                  />
                </Form.Item>
                <Button
                  icon={<DeleteOutlined />}
                  aria-label={t('workflow.builder.properties.validationMessages.remove')}
                  onClick={() => remove(row.name)}
                />
              </div>
              <Form.Item name={[row.name, 'en']} style={{ marginBottom: 0 }}>
                <Input
                  maxLength={500}
                  placeholder={t('workflow.builder.properties.validationMessages.en')}
                  aria-label={t('workflow.builder.properties.validationMessages.en')}
                />
              </Form.Item>
              <Form.Item name={[row.name, 'fr']} style={{ marginBottom: 0 }}>
                <Input
                  maxLength={500}
                  placeholder={t('workflow.builder.properties.validationMessages.fr')}
                  aria-label={t('workflow.builder.properties.validationMessages.fr')}
                />
              </Form.Item>
            </div>
          ))}
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => add({ key: 'required', en: '', fr: '' })}
          >
            {t('workflow.builder.properties.validationMessages.add')}
          </Button>
        </div>
      )}
    </Form.List>
  );
}
